import React from 'react';
import {
  Cursor, MapPin, Polygon, Rectangle, Circle, LineSegment,
  ArrowRight, Ruler, TextT, Mountains,
  ArrowCounterClockwise, ArrowClockwise, Camera, FilePdf, ListBullets,
  CraneTower, BuildingOffice, Package, Toilet, Trash
} from '@phosphor-icons/react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { SITE_LOGISTICS } from './mapUtils';
import BoundariesControl from './BoundariesControl';

function Btn({ icon: Icon, label, active, onClick, tid, disabled, sub, cls = '' }) {
  return (
    <button onClick={onClick} title={label} disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-all relative ${active ? 'bg-primary text-white shadow-sm' : disabled ? 'opacity-30 cursor-not-allowed' : `hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300 ${cls}`}`}
      data-testid={tid}>
      <Icon size={18} weight={active ? 'fill' : 'regular'} />
      {sub && <span className="absolute bottom-0 right-0.5 text-[7px] font-bold">{sub}</span>}
    </button>
  );
}

function BtnAngle({ active, onClick }) {
  return (
    <button onClick={onClick} title="Angle Measurement" className={`w-8 h-8 flex items-center justify-center rounded-md transition-all ${active ? 'bg-primary text-white shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300'}`} data-testid="tool-angle">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 20 L4 8 L16 20" /><path d="M4 16 Q8 16 8 20" /></svg>
    </button>
  );
}

function Sep() { return <div className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-0.5" />; }

const SITE_ICONS = {
  site_crane:   CraneTower,
  site_office:  BuildingOffice,
  site_storage: Package,
  site_toilet:  Toilet,
  site_skipbin: Trash,
};

function SiteLogisticsMenu({ customTool, onActivateCustomTool, panelCls }) {
  const isAnyActive = customTool && customTool.startsWith('site_');
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          title="Site Logistics"
          className={`w-8 h-8 flex items-center justify-center rounded-md transition-all relative ${isAnyActive ? 'bg-primary text-white shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300'}`}
          data-testid="tool-site-logistics"
        >
          <CraneTower size={18} weight={isAnyActive ? 'fill' : 'regular'} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className={`w-64 p-3 rounded-lg ${panelCls?.includes('bg-[#2F3349]') ? 'bg-[#2F3349] border-[#434968] text-[#CFD3EC]' : ''}`}>
        <div className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Site Logistics</div>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(SITE_LOGISTICS).map(([key, cfg]) => {
            const Icon = SITE_ICONS[key];
            const active = customTool === key;
            return (
              <button
                key={key}
                onClick={() => { onActivateCustomTool(key); setOpen(false); }}
                className={`flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-semibold transition-all ${active ? 'text-white shadow-sm' : 'hover:bg-gray-100 dark:hover:bg-white/10'}`}
                style={active ? { background: cfg.color } : {}}
                data-testid={`tool-${key}`}
              >
                <span
                  className="w-5 h-5 rounded-md flex items-center justify-center text-white shrink-0"
                  style={{ background: active ? 'rgba(255,255,255,0.25)' : cfg.color }}
                >
                  <Icon size={13} weight="fill" />
                </span>
                <span className="truncate">{cfg.label}</span>
              </button>
            );
          })}
        </div>
        <p className="mt-2.5 text-[10px] opacity-50 leading-relaxed">
          Click on the map to place.
        </p>
      </PopoverContent>
    </Popover>
  );
}

export default function MapToolbar({
  panelCls, drawingMode, customTool,
  undoStack, redoStack, onUndo, onRedo,
  onChangeDrawingMode, onActivateCustomTool, onSnapshot, onExportPDF,
  panelOpen, onTogglePanel, measurementCount,
  capabilities,
  boundariesEnabled, boundariesOpacity, boundariesColourIndex,
  onToggleBoundaries, onBoundariesOpacityChange, onBoundariesColourChange,
}) {
  const isActive = (mode) => drawingMode === mode && !customTool;
  const isCustomActive = (tool) => customTool === tool;
  // Default to all-enabled so the toolbar still works in legacy callsites
  // where capabilities were not yet wired (e.g. read-only sharers).
  const caps = capabilities || {
    perimeter: true, multiDistance: true, heightEstimation: true,
    angleMeasurement: true, siteLogistics: true, boundaries: true,
  };

  return (
    <div className={`${panelCls} rounded-md px-1 py-1 flex items-center gap-0.5 flex-wrap`} data-testid="map-draw-toolbar">
      <Btn icon={Cursor} label="Select" active={isActive(null) && !customTool} onClick={() => { onChangeDrawingMode(null); }} tid="tool-select" />
      <Sep />
      <Btn icon={TextT} label="Text Label" active={isCustomActive('text_label')} onClick={() => onActivateCustomTool('text_label')} tid="tool-text" />
      <Btn icon={MapPin} label="Marker" active={isCustomActive('add_marker')} onClick={() => onActivateCustomTool('add_marker')} tid="tool-marker" />
      <Btn icon={Polygon} label="Polygon" active={isActive('polygon')} onClick={() => onChangeDrawingMode('polygon')} tid="tool-polygon" />
      <Btn icon={Rectangle} label="Rectangle" active={isActive('rectangle')} onClick={() => onChangeDrawingMode('rectangle')} tid="tool-rectangle" />
      <Btn icon={Circle} label="Circle" active={isActive('circle')} onClick={() => onChangeDrawingMode('circle')} tid="tool-circle" />
      <Btn icon={LineSegment} label="Line" active={isActive('polyline')} onClick={() => onChangeDrawingMode('polyline')} tid="tool-polyline" />
      <Sep />
      <Btn icon={ArrowRight} label="Arrow" active={isCustomActive('arrow')} onClick={() => onActivateCustomTool('arrow')} tid="tool-arrow" />
      {caps.perimeter && <Btn icon={Ruler} label="Perimeter" active={isCustomActive('perimeter')} onClick={() => onActivateCustomTool('perimeter')} tid="tool-perimeter" />}
      {caps.multiDistance && <Btn icon={LineSegment} label="Multi-Dist" active={isCustomActive('multi_distance')} onClick={() => onActivateCustomTool('multi_distance')} tid="tool-multi-dist" sub="M" />}
      {caps.heightEstimation && <Btn icon={Mountains} label="Height Est." active={isCustomActive('height')} onClick={() => onActivateCustomTool('height')} tid="tool-height" />}
      {caps.angleMeasurement && <BtnAngle active={isCustomActive('angle')} onClick={() => onActivateCustomTool('angle')} />}
      {(caps.siteLogistics || caps.boundaries) && <Sep />}
      {caps.siteLogistics && <SiteLogisticsMenu customTool={customTool} onActivateCustomTool={onActivateCustomTool} panelCls={panelCls} />}
      {caps.boundaries && (
        <BoundariesControl
          panelCls={panelCls}
          enabled={!!boundariesEnabled}
          opacity={boundariesOpacity}
          colourIndex={boundariesColourIndex}
          onToggle={onToggleBoundaries}
          onOpacityChange={onBoundariesOpacityChange}
          onColourChange={onBoundariesColourChange}
        />
      )}
      <Sep />
      <Btn icon={ArrowCounterClockwise} label="Undo" onClick={onUndo} tid="tool-undo" disabled={undoStack.length === 0} />
      <Btn icon={ArrowClockwise} label="Redo" onClick={onRedo} tid="tool-redo" disabled={redoStack.length === 0} />
      <Sep />
      <Btn icon={Camera} label="Snapshot" onClick={onSnapshot} tid="tool-snapshot" />
      {onExportPDF && (
        <>
          <Sep />
          <Btn icon={FilePdf} label="Export PDF" onClick={onExportPDF} tid="tool-export-pdf" />
        </>
      )}
      {onTogglePanel && (
        <>
          <Sep />
          <Btn icon={ListBullets} label={panelOpen ? 'Hide Measurements' : `Measurements (${measurementCount || 0})`} active={panelOpen} onClick={onTogglePanel} tid="tool-toggle-panel" />
        </>
      )}
    </div>
  );
}
